import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Portal sign-out footer — used by every role-specific Nav (Patient, Doctor,
 * Hotel, Pharmacy, Laboratory, Admin). Shows the signed-in user's email
 * and role, plus a clear "Sign out" button that:
 *
 *   1. Calls useAuth().logout() (revokes the refresh token server-side,
 *      clears local storage tokens and hydrated user).
 *   2. Navigates to /login.
 *
 * Consumers pass a portal-scoped testid prefix so each portal's button gets
 * its own stable data-testid (e.g. `doctor-nav-signout-btn`). The optional
 * `onNavigate` callback closes any mobile drawer the button lives inside.
 */
export default function NavSignOut({ testid = "nav-signout-btn", onNavigate }) {
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success("Signed out");
    } catch {
      // useAuth.logout is best-effort — proceed regardless.
    }
    if (onNavigate) onNavigate();
    nav("/", { replace: true });
  };

  const roleLabel = (user?.role || "").replace(/_/g, " ");

  return (
    <div className="px-3 py-3 border-t border-forest/10 space-y-2">
      {user && (
        <div className="px-2 min-w-0" data-testid={`${testid}-account`}>
          <div className="text-xs text-stone-800 truncate">{user.full_name || user.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-stone-400 truncate">
            {roleLabel || user.email}
          </div>
        </div>
      )}
      <button
        type="button"
        data-testid={testid}
        onClick={handleSignOut}
        className="w-full inline-flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-stone-700 hover:text-red-700 hover:bg-red-50 transition"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        <span>Sign out</span>
      </button>
    </div>
  );
}
